import React, { useEffect, useState } from 'react';
import { NavActions } from '../components/NavBar/NavActions';
import { Toast } from '../components/Toast';
import type { ToastProps } from '../components/Toast';
import { Button } from '../components/SubscriptionAppButton';
import { IconButton } from '../components/SubscriptionAppIconButton';
import { GripVertical, Pencil, Trash2, Plus, X, ChevronDown, Check } from 'lucide-react';
interface Tier {
  id: string;
  name: string;
  threshold: number;
  multiplier: number;
  color: string;
  benefits: string[];
}
const COLOR_OPTIONS = [{
  name: 'Green',
  value: '#10B981'
}, {
  name: 'Blue',
  value: '#3B82F6'
}, {
  name: 'Purple',
  value: '#6F42C1'
}, {
  name: 'Gold',
  value: '#F59E0B'
}, {
  name: 'Red',
  value: '#EF4444'
}, {
  name: 'Grey',
  value: '#94A3B8'
}];
const WINE_TEMPLATE: Tier[] = [{
  id: 'enthusiast',
  name: 'Enthusiast',
  threshold: 0,
  multiplier: 1.0,
  color: '#10B981',
  benefits: ['Free Shipping']
}, {
  id: 'connoisseur',
  name: 'Connoisseur',
  threshold: 500,
  multiplier: 1.25,
  color: '#3B82F6',
  benefits: ['Free Shipping', 'Early Access']
}, {
  id: 'sommelier',
  name: 'Sommelier',
  threshold: 2000,
  multiplier: 1.5,
  color: '#6F42C1',
  benefits: ['Free Shipping', 'Early Access', 'Priority Support']
}, {
  id: 'vintner',
  name: 'Vintner',
  threshold: 5000,
  multiplier: 2.0,
  color: '#F59E0B',
  benefits: ['Free Shipping', 'Early Access', 'Priority Support', 'Exclusive Events']
}];
export function LoyaltyRulesPage() {
  const [tiersEnabled, setTiersEnabled] = useState(true);
  const [calculationMethod, setCalculationMethod] = useState('Annual Spend');
  const [downgradePolicy, setDowngradePolicy] = useState('permanent');
  const [expandedTierId, setExpandedTierId] = useState<string | null>(null);
  const [earningModel, setEarningModel] = useState('everything');
  const [bonusToggles, setBonusToggles] = useState<Record<string, boolean>>({
    signup: true,
    referrer: true,
    referee: true,
    review: true,
    birthday: true,
    order3: true,
    order6: true,
    order12: true
  });
  const [experienceEnabled, setExperienceEnabled] = useState(true);
  const [toasts, setToasts] = useState<ToastProps[]>([]);
  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };
  const handleSave = () => {
    setToasts(prev => [...prev, {
      id: Date.now().toString(),
      type: 'success' as const,
      title: 'Rules saved',
      message: 'Your loyalty rules and tier configuration have been saved successfully.',
      onClose: removeToast
    }]);
  };
  const [addingBenefitForTier, setAddingBenefitForTier] = useState<string | null>(null);
  const [newBenefitText, setNewBenefitText] = useState('');
  const [tiers, setTiers] = useState<Tier[]>([{
    id: 'enthusiast',
    name: 'Enthusiast',
    threshold: 0,
    multiplier: 1.0,
    color: '#10B981',
    benefits: ['Free Shipping']
  }, {
    id: 'connoisseur',
    name: 'Connoisseur',
    threshold: 500,
    multiplier: 1.25,
    color: '#3B82F6',
    benefits: ['Free Shipping', 'Early Access']
  }, {
    id: 'sommelier',
    name: 'Sommelier',
    threshold: 2000,
    multiplier: 1.5,
    color: '#6F42C1',
    benefits: ['Free Shipping', 'Early Access', 'Priority Support']
  }, {
    id: 'vintner',
    name: 'Vintner',
    threshold: 5000,
    multiplier: 2.0,
    color: '#F59E0B',
    benefits: ['Free Shipping', 'Early Access', 'Priority Support', 'Exclusive Events']
  }]);
  // Editing state per tier
  const [editValues, setEditValues] = useState<Record<string, {
    name: string;
    threshold: string;
    multiplier: string;
    color: string;
    benefits: string[];
  }>>({});
  useEffect(() => {
    if (toasts.length > 0) {
      const timer = setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== prev[prev.length - 1].id));
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [toasts]);
  const startEditing = (tier: Tier) => {
    setEditValues(prev => ({
      ...prev,
      [tier.id]: {
        name: tier.name,
        threshold: String(tier.threshold),
        multiplier: String(tier.multiplier),
        color: tier.color,
        benefits: [...tier.benefits]
      }
    }));
    setExpandedTierId(tier.id);
  };
  const finishEditing = (tierId: string) => {
    const vals = editValues[tierId];
    if (vals) {
      setTiers(prev => prev.map(t => t.id === tierId ? {
        ...t,
        name: vals.name || t.name,
        threshold: Number(vals.threshold) || 0,
        multiplier: Number(vals.multiplier) || 1,
        color: vals.color,
        benefits: vals.benefits
      } : t));
    }
    setExpandedTierId(null);
    setAddingBenefitForTier(null);
    setNewBenefitText('');
  };
  const updateEditValue = (tierId: string, field: string, value: string | string[]) => {
    setEditValues(prev => ({
      ...prev,
      [tierId]: {
        ...prev[tierId],
        [field]: value
      }
    }));
  };
  const removeBenefit = (tierId: string, benefit: string) => {
    setEditValues(prev => ({
      ...prev,
      [tierId]: {
        ...prev[tierId],
        benefits: prev[tierId].benefits.filter(b => b !== benefit)
      }
    }));
  };
  const addBenefit = (tierId: string) => {
    if (newBenefitText.trim()) {
      setEditValues(prev => ({
        ...prev,
        [tierId]: {
          ...prev[tierId],
          benefits: [...prev[tierId].benefits, newBenefitText.trim()]
        }
      }));
      setNewBenefitText('');
      setAddingBenefitForTier(null);
    }
  };
  const deleteTier = (tierId: string) => {
    setTiers(prev => prev.filter(t => t.id !== tierId));
    setDeletingTierId(null);
    if (expandedTierId === tierId) setExpandedTierId(null);
  };
  const addNewTier = () => {
    if (tiers.length >= 5) return;
    const newId = `tier-${Date.now()}`;
    const newTier: Tier = {
      id: newId,
      name: '',
      threshold: 0,
      multiplier: 1.0,
      color: '#94A3B8',
      benefits: []
    };
    setTiers(prev => [...prev, newTier]);
    startEditing(newTier);
  };
  const applyWineTemplate = () => {
    setTiers(WINE_TEMPLATE.map(t => ({
      ...t,
      benefits: [...t.benefits]
    })));
    setShowTemplateConfirm(false);
    setExpandedTierId(null);
  };
  const bonusEvents = [{
    id: 'signup',
    name: 'Sign-Up Bonus',
    points: 500
  }, {
    id: 'referrer',
    name: 'Referral — Referrer',
    points: 1000
  }, {
    id: 'referee',
    name: 'Referral — Referee',
    points: 500
  }, {
    id: 'review',
    name: 'Product Review',
    points: 50,
    note: 'Cap 5 per month'
  }, {
    id: 'birthday',
    name: 'Birthday Bonus',
    points: 200
  }, {
    id: 'order3',
    name: 'After 3rd Order',
    points: 100
  }, {
    id: 'order6',
    name: 'After 6th Order',
    points: 200
  }, {
    id: 'order12',
    name: 'After 12th Order',
    points: 500
  }];
  const experienceTypes = [{
    name: 'Cellar Door Visit',
    points: 50
  }, {
    name: 'Standard Tasting',
    points: 100
  }, {
    name: 'Premium/Reserve Tasting',
    points: 200
  }, {
    name: 'Vineyard Tour',
    points: 150
  }, {
    name: 'Blending Workshop',
    points: 300
  }, {
    name: 'Winemaker Dinner',
    points: 500
  }];
  const toggleBonus = (id: string) => {
    setBonusToggles(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const [showTemplateConfirm, setShowTemplateConfirm] = useState(false);
  const [deletingTierId, setDeletingTierId] = useState<string | null>(null);
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-6995">
      {/* Header */}
      <div className="mb-6" data-id="element-6996">
        <div className="w-full h-16 bg-white border-b border-[#CBD5E1] px-6 flex items-center gap-4" data-id="element-6997">
          <h1 className="text-lg font-semibold text-[#0F172A] m-0" data-id="element-6998">
            Rules & Tiers
          </h1>
          <div className="flex-1" data-id="element-6999" />
          <Button variant="primary" size="md" onClick={handleSave} data-id="element-7000">
            Save Changes
          </Button>
          <NavActions data-id="element-7001" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto space-y-6" data-id="element-7002">
        {/* SECTION 1 — TIERS */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 shadow-sm" data-id="element-7003">
          <h2 className="text-base font-semibold text-[#0F172A] mb-4" data-id="element-7004">Tiers</h2>

          {/* Top Row */}
          <div className="flex items-center gap-4 mb-6" data-id="element-7005">
            <div className="flex items-center gap-3" data-id="element-7006">
              <span className="text-sm font-medium text-[#0F172A]" data-id="element-7007">
                Enable Tiers
              </span>
              <button onClick={() => setTiersEnabled(!tiersEnabled)} className={`w-11 h-6 rounded-full transition-colors duration-200 ease-in-out relative ${tiersEnabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-7008">
                <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ease-in-out ${tiersEnabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-7009" />
              </button>
            </div>

            <div className="flex items-center gap-2" data-id="element-7010">
              <span className="text-sm font-medium text-[#0F172A]" data-id="element-7011">
                Calculation Method
              </span>
              <div className="relative w-[200px]" data-id="element-7012">
                <select value={calculationMethod} onChange={e => setCalculationMethod(e.target.value)} className="w-full h-9 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white" data-id="element-7013">
                  <option data-id="element-7014">Annual Spend</option>
                  <option data-id="element-7015">Lifetime Spend</option>
                  <option data-id="element-7016">Points Balance</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-7017" />
              </div>
            </div>

            <div className="flex-1 flex justify-end relative" data-id="element-7018">
              <Button variant="secondary" size="sm" onClick={() => setShowTemplateConfirm(true)} data-id="element-7019">
                Use Wine Template
              </Button>

              {/* Wine Template Confirmation Popover */}
              {showTemplateConfirm && <div className="absolute top-full right-0 mt-2 w-72 bg-white border border-[#E2E8F0] rounded-lg shadow-lg p-4 z-20" data-id="element-7020">
                  <p className="text-sm text-[#0F172A] mb-3" data-id="element-7021">
                    This will replace your current tiers. Continue?
                  </p>
                  <div className="flex justify-end gap-2" data-id="element-7022">
                    <Button variant="ghost" size="sm" onClick={() => setShowTemplateConfirm(false)} data-id="element-7023">
                      Cancel
                    </Button>
                    <Button variant="primary" size="sm" onClick={applyWineTemplate} data-id="element-7024">
                      Replace Tiers
                    </Button>
                  </div>
                </div>}
            </div>
          </div>

          {/* Downgrade Policy */}
          <div className="mb-6" data-id="element-7025">
            <span className="text-sm font-medium text-[#0F172A] block mb-2" data-id="element-7026">
              Downgrade Policy
            </span>
            <div className="flex items-center gap-6" data-id="element-7027">
              <label className="flex items-center gap-2 cursor-pointer" data-id="element-7028">
                <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${downgradePolicy === 'permanent' ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => setDowngradePolicy('permanent')} data-id="element-7029">
                  {downgradePolicy === 'permanent' && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-7030" />}
                </div>
                <span className="text-sm text-[#0F172A]" data-id="element-7031">
                  Once earned, permanent
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-id="element-7032">
                <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${downgradePolicy === 'periodic' ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => setDowngradePolicy('periodic')} data-id="element-7033">
                  {downgradePolicy === 'periodic' && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-7034" />}
                </div>
                <span className="text-sm text-[#0F172A]" data-id="element-7035">
                  Re-evaluate periodically
                </span>
              </label>
            </div>
          </div>

          {/* Tier Rows */}
          <div className="space-y-0" data-id="element-7036">
            {tiers.map(tier => {
            const ev = editValues[tier.id];
            return <div key={tier.id} data-id="element-7037">
                  <div className="flex items-center gap-4 py-3 border-b border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors group relative" data-id="element-7038">
                    <GripVertical className="w-4 h-4 text-[#94A3B8] cursor-grab" data-id="element-7039" />
                    <div className="w-3 h-3 rounded-full" style={{
                  backgroundColor: tier.color
                }} data-id="element-7040" />
                    <span className="text-sm font-medium text-[#0F172A] flex-1" data-id="element-7041">
                      {tier.name || <span className="text-[#94A3B8] italic" data-id="element-7042">New Tier</span>}
                    </span>
                    <span className="text-sm text-[#475569] w-24 text-right" data-id="element-7043">
                      ${tier.threshold.toLocaleString()}
                    </span>
                    <span className="text-sm text-[#475569] w-16 text-right" data-id="element-7044">
                      {tier.multiplier}x
                    </span>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-7045">
                      <button onClick={() => {
                    if (expandedTierId === tier.id) {
                      finishEditing(tier.id);
                    } else {
                      startEditing(tier);
                    }
                  }} className="p-1.5 rounded hover:bg-[#E0F2FE] text-[#94A3B8] hover:text-[#0EA5E9] transition-colors" data-id="element-7046">
                        <Pencil className="w-4 h-4" data-id="element-7047" />
                      </button>
                      <button onClick={() => setDeletingTierId(deletingTierId === tier.id ? null : tier.id)} className="p-1.5 rounded hover:bg-[#FEE2E2] text-[#94A3B8] hover:text-[#EF4444] transition-colors" data-id="element-7048">
                        <Trash2 className="w-4 h-4" data-id="element-7049" />
                      </button>
                    </div>

                    {/* Delete Confirmation Popover */}
                    {deletingTierId === tier.id && <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-[#E2E8F0] rounded-lg shadow-lg p-4 z-20" data-id="element-7050">
                        <p className="text-sm text-[#0F172A] mb-1 font-medium" data-id="element-7051">
                          Delete this tier?
                        </p>
                        <p className="text-xs text-[#64748B] mb-3" data-id="element-7052">
                          Members will be moved to the tier below.
                        </p>
                        <div className="flex justify-end gap-2" data-id="element-7053">
                          <Button variant="ghost" size="sm" onClick={() => setDeletingTierId(null)} data-id="element-7054">
                            Cancel
                          </Button>
                          <Button variant="danger" size="sm" onClick={() => deleteTier(tier.id)} data-id="element-7055">
                            Delete
                          </Button>
                        </div>
                      </div>}
                  </div>

                  {/* Expanded Edit State */}
                  {expandedTierId === tier.id && ev && <div className="ml-8 mt-3 mb-3 p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]" data-id="element-7056">
                      <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-7057">
                        <div data-id="element-7058">
                          <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-7059">
                            Name
                          </label>
                          <input type="text" value={ev.name} onChange={e => updateEditValue(tier.id, 'name', e.target.value)} placeholder="Tier name" className="w-full h-9 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-7060" />
                        </div>
                        <div data-id="element-7061">
                          <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-7062">
                            Threshold
                          </label>
                          <div className="relative" data-id="element-7063">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#94A3B8]" data-id="element-7064">
                              $
                            </span>
                            <input type="number" value={ev.threshold} onChange={e => updateEditValue(tier.id, 'threshold', e.target.value)} className="w-full h-9 pl-7 pr-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-7065" />
                          </div>
                        </div>
                        <div data-id="element-7066">
                          <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-7067">
                            Multiplier
                          </label>
                          <div className="relative" data-id="element-7068">
                            <input type="number" value={ev.multiplier} onChange={e => updateEditValue(tier.id, 'multiplier', e.target.value)} step="0.1" className="w-full h-9 pl-3 pr-7 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-7069" />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#94A3B8]" data-id="element-7070">
                              x
                            </span>
                          </div>
                        </div>
                        <div data-id="element-7071">
                          <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-7072">
                            Colour
                          </label>
                          <div className="flex items-center gap-2" data-id="element-7073">
                            {COLOR_OPTIONS.map(c => <button key={c.value} onClick={() => updateEditValue(tier.id, 'color', c.value)} className={`w-7 h-7 rounded-full border-2 transition-all ${ev.color === c.value ? 'border-[#0F172A] scale-110' : 'border-transparent hover:scale-105'}`} style={{
                        backgroundColor: c.value
                      }} title={c.name} data-id="element-7074" />)}
                          </div>
                        </div>
                      </div>

                      <div className="mb-4" data-id="element-7075">
                        <label className="block text-xs font-medium text-[#475569] mb-2" data-id="element-7076">
                          Benefits
                        </label>
                        <div className="flex flex-wrap gap-2" data-id="element-7077">
                          {ev.benefits.map(benefit => <div key={benefit} className="flex items-center gap-1 bg-[#E0F2FE] text-[#0EA5E9] px-2.5 py-1 rounded-full text-xs font-medium" data-id="element-7078">
                              {benefit}
                              <button onClick={() => removeBenefit(tier.id, benefit)} className="hover:text-[#0369A1]" data-id="element-7079">
                                <X className="w-3 h-3" data-id="element-7080" />
                              </button>
                            </div>)}
                          {addingBenefitForTier === tier.id ? <div className="flex items-center gap-1" data-id="element-7081">
                              <input type="text" value={newBenefitText} onChange={e => setNewBenefitText(e.target.value)} onKeyDown={e => {
                        if (e.key === 'Enter') addBenefit(tier.id);
                      }} placeholder="Benefit name" autoFocus className="h-7 w-32 px-2 border border-[#CBD5E1] rounded-md text-xs text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-7082" />
                              <button onClick={() => addBenefit(tier.id)} className="h-6 w-6 rounded-full bg-[#0EA5E9] text-white flex items-center justify-center hover:bg-[#0284C7] transition-colors" data-id="element-7083">
                                <Check className="w-3 h-3" data-id="element-7084" />
                              </button>
                              <button onClick={() => {
                        setAddingBenefitForTier(null);
                        setNewBenefitText('');
                      }} className="h-6 w-6 rounded-full text-[#94A3B8] flex items-center justify-center hover:text-[#EF4444] transition-colors" data-id="element-7085">
                                <X className="w-3 h-3" data-id="element-7086" />
                              </button>
                            </div> : <button onClick={() => setAddingBenefitForTier(tier.id)} className="w-6 h-6 rounded-full border border-dashed border-[#CBD5E1] flex items-center justify-center text-[#94A3B8] hover:border-[#0EA5E9] hover:text-[#0EA5E9] transition-colors" data-id="element-7087">
                              <Plus className="w-3 h-3" data-id="element-7088" />
                            </button>}
                        </div>
                      </div>

                      <div className="flex justify-end" data-id="element-7089">
                        <Button variant="ghost" size="sm" onClick={() => finishEditing(tier.id)} data-id="element-7090">
                          Done
                        </Button>
                      </div>
                    </div>}
                </div>;
          })}
          </div>

          <div className="mt-4 flex items-center gap-2" data-id="element-7091">
            <Button variant="ghost" size="sm" onClick={addNewTier} disabled={tiers.length >= 5} iconLeft={<Plus className="w-4 h-4" data-id="element-7093" />} data-id="element-7092">
              Add Tier
            </Button>
            <span className="text-xs text-[#94A3B8]" data-id="element-7094">(max 5)</span>
          </div>
        </div>

        {/* SECTION 2 — BASE EARNING RATE */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 shadow-sm" data-id="element-7095">
          <h2 className="text-base font-semibold text-[#0F172A] mb-4" data-id="element-7096">
            Base Earning Rate
          </h2>
          <div className="flex items-center gap-4" data-id="element-7097">
            <span className="text-sm font-medium text-[#0F172A]" data-id="element-7098">
              Points per dollar spent
            </span>
            <input type="number" defaultValue="10" className="w-20 h-10 text-center border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-7099" />
          </div>
          <p className="text-xs text-[#94A3B8] mt-2" data-id="element-7100">
            A $50 order earns 500 points (before tier multipliers)
          </p>
        </div>

        {/* SECTION 3 — PRODUCT EARNING MODEL */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 shadow-sm" data-id="element-7101">
          <h2 className="text-base font-semibold text-[#0F172A] mb-4" data-id="element-7102">
            Product Earning Model
          </h2>
          <div className="space-y-4" data-id="element-7103">
            {[{
            id: 'everything',
            title: 'Earn on Everything',
            desc: 'All products earn at the base rate'
          }, {
            id: 'selected',
            title: 'Earn on Selected Only',
            desc: 'Only whitelisted collections/products earn'
          }, {
            id: 'exclude',
            title: 'Exclude Selected',
            desc: 'Everything earns except blacklisted items'
          }, {
            id: 'tiered',
            title: 'Tiered by Collection',
            desc: 'Different rates per collection'
          }].map(option => <div key={option.id} data-id="element-7104">
                <label className="flex items-center gap-3 cursor-pointer mb-1" data-id="element-7105">
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${earningModel === option.id ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => setEarningModel(option.id)} data-id="element-7106">
                    {earningModel === option.id && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-7107" />}
                  </div>
                  <span className="text-sm font-medium text-[#0F172A]" data-id="element-7108">
                    {option.title}
                  </span>
                </label>
                <p className="text-xs text-[#64748B] ml-7" data-id="element-7109">{option.desc}</p>
              </div>)}
          </div>
        </div>

        {/* SECTION 4 — BONUS EVENTS */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 shadow-sm" data-id="element-7110">
          <h2 className="text-base font-semibold text-[#0F172A] mb-4" data-id="element-7111">
            Bonus Events
          </h2>
          <div className="space-y-0" data-id="element-7112">
            {bonusEvents.map(event => <div key={event.id} className="flex items-center justify-between py-3 border-b border-[#E2E8F0]" data-id="element-7113">
                <div className="flex-1" data-id="element-7114">
                  <span className="text-sm font-medium text-[#0F172A]" data-id="element-7115">
                    {event.name}
                  </span>
                  {event.note && <span className="text-xs text-[#94A3B8] ml-2" data-id="element-7116">
                      ({event.note})
                    </span>}
                </div>
                <div className="flex items-center gap-4" data-id="element-7117">
                  <button onClick={() => toggleBonus(event.id)} className={`w-11 h-6 rounded-full transition-colors duration-200 ease-in-out relative ${bonusToggles[event.id] ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-7118">
                    <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ease-in-out ${bonusToggles[event.id] ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-7119" />
                  </button>
                  <div className="flex items-center gap-2" data-id="element-7120">
                    <input type="number" defaultValue={event.points} className="w-20 h-8 text-center border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-7121" />
                    <span className="text-sm text-[#475569] w-6" data-id="element-7122">pts</span>
                  </div>
                </div>
              </div>)}
          </div>
          <Button variant="ghost" size="sm" iconLeft={<Plus className="w-4 h-4" data-id="element-7124" />} data-id="element-7123">
            Add Custom Milestone
          </Button>
        </div>

        {/* SECTION 5 — EXPERIENCE-BASED EARNING */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 shadow-sm" data-id="element-7125">
          <div className="flex items-center justify-between mb-4" data-id="element-7126">
            <h2 className="text-base font-semibold text-[#0F172A]" data-id="element-7127">
              Experience-Based Earning
            </h2>
            <div className="flex items-center gap-3" data-id="element-7128">
              <span className="text-sm font-medium text-[#0F172A]" data-id="element-7129">
                Enabled
              </span>
              <button onClick={() => setExperienceEnabled(!experienceEnabled)} className={`w-11 h-6 rounded-full transition-colors duration-200 ease-in-out relative ${experienceEnabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-7130">
                <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ease-in-out ${experienceEnabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-7131" />
              </button>
            </div>
          </div>

          <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-7132">
            <table className="w-full" data-id="element-7133">
              <thead className="bg-[#F1F5F9]" data-id="element-7134">
                <tr data-id="element-7135">
                  <th className="py-2 px-3 text-left text-xs uppercase tracking-wider font-semibold text-[#94A3B8]" data-id="element-7136">
                    Experience Type
                  </th>
                  <th className="py-2 px-3 text-right text-xs uppercase tracking-wider font-semibold text-[#94A3B8] w-24" data-id="element-7137">
                    Base Points
                  </th>
                  <th className="py-2 px-3 text-center text-xs uppercase tracking-wider font-semibold text-[#94A3B8] w-32" data-id="element-7138">
                    Tier Multiplier Applied
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]" data-id="element-7139">
                {experienceTypes.map((exp, index) => <tr key={index} data-id="element-7140">
                    <td className="py-3 px-3 text-sm text-[#0F172A]" data-id="element-7141">
                      {exp.name}
                    </td>
                    <td className="py-3 px-3" data-id="element-7142">
                      <input type="number" defaultValue={exp.points} className="w-20 h-8 text-center border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none float-right" data-id="element-7143" />
                    </td>
                    <td className="py-3 px-3 text-center" data-id="element-7144">
                      <span className="text-sm font-medium text-[#10B981]" data-id="element-7145">
                        Yes
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <Button variant="ghost" size="sm" iconLeft={<Plus className="w-4 h-4" data-id="element-7147" />} data-id="element-7146">
            Add Experience Type
          </Button>
        </div>
      </div>

      {/* Toasts - replaces inline toast */}
      {toasts.map(toast => <Toast key={toast.id} {...toast} onClose={removeToast} data-id="element-7148" />)}
    </div>;
}